/**
 * Per-item disclosure state for the primary navigation's drop-down menus.
 *
 * Each top-level menu item that has a sub-menu is its own `navAccordion`
 * scope ( wired up by the PHP nav walker ). The toggle button flips the item
 * open, which on mobile reveals the sub-menu via the `.is-submenu-open` class
 * ( animated in CSS ) and keeps the button's `aria-expanded` honest.
 *
 * Open state lives in a shared store keyed by the active item's id, so only
 * one accordion can be open at a time on mobile — opening one closes any
 * other. `open` is a getter that simply asks the store whether this item is
 * the active one.
 *
 * On desktop the drop-down opens on hover / focus through CSS alone, so this
 * component does nothing there beyond tracking `open` for the toggle button —
 * which is itself hidden at desktop widths.
 *
 * Open accordions close when the viewport crosses up into desktop range, so a
 * panel left open on mobile does not strand `open` state behind the hover UI.
 *
 * Usage ( emitted by the walker ):
 *   <li x-data="navAccordion" :class="{ 'is-submenu-open': open }">
 *     <a>…</a>
 *     <button @click="toggle" :aria-expanded="open.toString()">…</button>
 *     <ul class="sub-menu">…</ul>
 *   </li>
 */

document.addEventListener( 'alpine:init', () => {

	// Holds the id of the single open accordion, or null when all are closed.
	Alpine.store( 'navAccordion', { active: null });

	// Hands every accordion instance a distinct id. ( Alpine's $id() returns
	// the same value for sibling x-data scopes without an x-id boundary, which
	// would make them all match the store's active key at once. )
	let uid = 0;

	Alpine.data( 'navAccordion', () => ({

		// A stable per-instance id used as the store's "active" key. Assigned at
		// data-creation time ( not in init ) so it is a real, declared property
		// on each component rather than one added imperatively to the proxy.
		id: ++uid,

		init() {

			// A panel toggled open on mobile should not stay open once the
			// layout switches to the hover-driven desktop dropdown.
			this.desktop = window.matchMedia( '(min-width: 80rem)' );

			this.onChange = ( event ) => {
				if ( event.matches && this.open ) {
					Alpine.store( 'navAccordion' ).active = null;
				}
			};

			this.desktop.addEventListener( 'change', this.onChange );

			this.$cleanup = () => this.desktop.removeEventListener( 'change', this.onChange );
		},

		get open() {
			return Alpine.store( 'navAccordion' ).active === this.id;
		},

		toggle() {
			const store = Alpine.store( 'navAccordion' );

			store.active = this.open ? null : this.id;
		},

	}) );
});
